<template>
  <ModalComponent
    ref="modalRef"
    size="xl"
    submitButtonText=""
    noFooter
    :beforeClose="mayClose"
    @close="finish"
  >
    <template #title>{{ heading }}</template>
    <template #body>
      <LoadingIndicatorComponent
        v-if="editor.loading"
        :message="$t('forwards.mandates.editor.loading')"
      />

      <div v-else-if="editor.notFound" class="text-center py-5">
        <FontAwesomeIcon
          icon="fa-solid fa-lock"
          size="2x"
          class="text-body-tertiary mb-3"
        />
        <h5>{{ $t("forwards.mandates.editor.not_found_title") }}</h5>
        <p class="text-body-tertiary mb-0">
          {{ $t("forwards.mandates.editor.not_found_body") }}
        </p>
      </div>

      <template v-else>
        <div
          v-if="infeasiblePeriods.length > 0"
          class="advisory-note mb-3 infeasibility-warning"
          role="status"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{
            $t("forwards.mandates.infeasible_periods", {
              periods: infeasiblePeriods.join(", "),
            })
          }}</span>
        </div>

        <MandateEscrowRemainingComponent
          v-if="editor.escrowRemaining !== null"
          :rows="editor.escrowRemaining"
        />

        <template v-else>
          <WizardStepRail
            v-if="inWizard"
            :steps="wizardSteps"
            :currentIndex="step"
            :stepLabel="$t('common.step')"
            class="mb-4"
          />

          <div
            v-else
            class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3"
          >
            <p class="text-body-tertiary mb-0">
              {{ $t("forwards.mandates.editor.description") }}
            </p>
            <div
              v-if="editor.canEdit && !editor.isNew"
              class="d-flex align-items-center gap-2 flex-wrap"
            >
              <!-- A mandate that cannot be deleted can be archived, so its blocked reason is not shown. -->
              <ActionButtonWithSpinner
                v-if="editor.capabilities?.can_delete"
                :busy="editor.busyAction === 'delete'"
                variant="btn-outline-danger"
                @click="onDelete"
              >
                {{ $t("forwards.mandates.editor.delete") }}
              </ActionButtonWithSpinner>
              <ActionButtonWithSpinner
                v-if="editor.capabilities?.can_archive"
                :busy="editor.busyAction === 'archive'"
                variant="btn-ghost"
                @click="onArchive"
              >
                {{ $t("forwards.mandates.editor.archive") }}
              </ActionButtonWithSpinner>
              <ActionButtonWithSpinner
                v-if="editor.capabilities?.can_unarchive"
                :busy="editor.busyAction === 'unarchive'"
                variant="btn-ghost"
                @click="onUnarchive"
              >
                {{ $t("forwards.mandates.editor.unarchive") }}
              </ActionButtonWithSpinner>
            </div>
          </div>

          <div v-if="!editor.canEdit" class="advisory-note mb-3">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" />
            <span>{{ $t("forwards.mandates.editor.read_only") }}</span>
          </div>

          <div
            v-if="editor.saveError !== null"
            class="error-note mb-3"
            role="alert"
          >
            <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
            <span>{{
              editor.saveErrorMessage ??
              $t("forwards.mandates.editor.save_failed")
            }}</span>
          </div>

          <template v-if="shows('warnings')">
            <p
              v-for="(warning, index) in allWarnings"
              :key="index"
              class="advisory-note mb-2"
            >
              <FontAwesomeIcon icon="fa-regular fa-circle-info" />
              <span>{{ warning }}</span>
            </p>
          </template>

          <!-- One template for both views, as the PPA editor: the wizard shows a step's sections, the
               full form every one (`visibleSections`); the form state lives in the editor, so a step
               left and returned to keeps what was entered. -->
          <div class="d-flex flex-column gap-4 pb-4">
            <!-- A new mandate's first step: someone creating one may be new to forward sales. -->
            <ForwardSalesGuideComponent
              v-if="inWizard && step === 0"
              class="wizard-guide"
            />

            <WizardSection
              v-if="shows('details')"
              :title="$t('forwards.mandates.editor.details_title')"
            >
              <div class="mb-3">
                <label class="form-label" :for="nameId"
                  >{{ $t("forwards.mandates.editor.name") }}
                  <InfoTooltip
                    v-if="editor.canEdit"
                    :text="$t('forwards.mandates.editor.name_hint')"
                /></label>
                <input
                  :id="nameId"
                  :value="editor.form.name"
                  type="text"
                  class="form-control"
                  :maxlength="MANDATE_NAME_MAX"
                  :class="{ 'is-invalid': editor.form.name.trim() === '' }"
                  :disabled="!editor.canEdit"
                  @input="onNameInput"
                />
              </div>
              <div>
                <label class="form-label" :for="descriptionId"
                  >{{ $t("forwards.mandates.editor.mandate_description") }}
                  <InfoTooltip
                    v-if="editor.canEdit"
                    :text="
                      $t('forwards.mandates.editor.mandate_description_hint')
                    "
                /></label>
                <textarea
                  :id="descriptionId"
                  v-model="descriptionText"
                  class="form-control"
                  rows="3"
                  :maxlength="MANDATE_DESCRIPTION_MAX"
                  :disabled="!editor.canEdit"
                ></textarea>
              </div>
            </WizardSection>

            <WizardSection
              v-if="shows('options')"
              :title="$t('forwards.mandates.options.title')"
            >
              <MandateOptionsCardComponent
                v-model:minimumPrice="editor.form.minimum_price"
                v-model:contangoCondition="editor.form.contango_condition"
                v-model:archiveOnDelivery="editor.form.archive_on_delivery"
                :minimumPriceError="editor.minimumPriceProblem"
                :disabled="!editor.canEdit"
              />
              <MandateAdvancedListingComponent
                v-if="isStaff"
                v-model="editor.form.advanced_listing"
                :simpleShape="simpleShape"
                :disabled="!editor.canEdit"
                class="mt-3"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('members')"
              :title="$t('forwards.mandates.members.title')"
              :description="$t('forwards.mandates.members.description')"
            >
              <MandateMembersPickerComponent
                v-model="editor.form.member_company_ids"
                :candidates="editor.memberCandidates"
                :blockers="editor.blockers"
                :disabled="!editor.canEdit"
                :locked="editor.membersLocked"
                :ownCompaniesOnly="editor.offersOwnCompaniesOnly"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('devices')"
              :title="$t('forwards.mandates.devices.title')"
              :description="$t('forwards.mandates.devices.description')"
            >
              <MandateDevicePanelComponent
                v-model:deviceScope="editor.form.device_scope"
                v-model:allowlistDeviceIds="editor.form.allowlist_device_ids"
                :devices="editor.devices"
                :loading="editor.devicesLoading"
                :disabled="!editor.canEdit"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('listing')"
              :title="$t('forwards.mandates.volumes.title')"
            >
              <!-- The full form offers the box among the options; the wizard has none, so its listing step does. -->
              <MandateAdvancedListingComponent
                v-if="isStaff && inWizard"
                v-model="editor.form.advanced_listing"
                :simpleShape="simpleShape"
                :disabled="!editor.canEdit"
                class="mb-3"
              />
              <MandateVolumesEditorComponent
                :listingKind="editor.form.listing_kind"
                :entryWidth="editor.entryWidth"
                @select:kind="editor.setListingKind"
                @select:width="editor.setEntryWidth"
                v-model:recurringVolumes="editor.recurringDraft.volumes"
                v-model:listings="editor.oneOffListings"
                :preview="editor.preview"
                :previewLoading="editor.previewLoading"
                :listingChanged="editor.listingChanged"
                :disabled="!editor.canEdit"
                :simple="simpleListing"
              />
              <!-- In the simple editor too: a production range is not a listing shape (`hasSimpleShape`). -->
              <MandateProductionRangeComponent
                v-if="editor.form.listing_kind !== 'one_off'"
                v-model:limited="editor.form.production_limited"
                v-model:from="editor.form.production_from"
                v-model:until="editor.form.production_until"
                :error="editor.productionRangeProblem"
                :disabled="!editor.canEdit"
                class="mt-3"
              />
              <!-- The wizard creates a mandate on the default strategy; it is chosen afterwards in the full form. -->
              <MandateSalesStrategyComponent
                v-if="!inWizard && !payAsProduced"
                v-model="editor.form.sales_plan"
                :committedAboveRelease="editor.preview?.committed_above_release"
                :releaseChanges="editor.preview?.release_changes"
                :sellAhead="!simpleListing"
                :disabled="!editor.canEdit"
                class="mt-3"
              />
              <!-- The wizard shows the schedule in its summary instead, beside the listing it releases. -->
              <MandateSalesScheduleButtonComponent
                v-if="!inWizard && !payAsProduced"
                :schedule="salesSchedule"
                class="mt-3"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('name')"
              :title="$t('forwards.mandates.editor.name')"
            >
              <MandateNameFieldComponent
                :name="editor.form.name"
                :edited="editor.nameEdited"
                @edit="editor.setName"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('summary')"
              :title="$t('forwards.mandates.summary.title')"
            >
              <MandateSummaryComponent
                :form="editor.form"
                :salesSchedule
                :memberNames="editor.memberNames"
                :devices="editor.mandateDevices"
              />
              <p
                v-for="blocker in editor.blockers"
                :key="blocker"
                class="error-note mt-3 mb-0"
              >
                <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
                <span>{{ blocker }}</span>
              </p>
            </WizardSection>

            <WizardSection
              v-if="shows('eligibility') && eligibility"
              :title="$t('forwards.mandates.eligibility.title')"
              :description="$t('forwards.mandates.eligibility.description')"
            >
              <MandateEligibilityIndicatorComponent
                :indicator="eligibility"
                :devices="editor.mandateDevices"
              />
              <MandateEligibilityGuideComponent class="mt-3" />
            </WizardSection>

            <WizardSection
              v-if="shows('commitments')"
              :title="$t('forwards.mandates.commitments.title')"
              :description="$t('forwards.mandates.commitments.description')"
            >
              <MandateCommitmentsTableComponent
                :rows="editor.commitments"
                :loading="editor.loading"
              />
            </WizardSection>
          </div>
        </template>
      </template>

      <WizardFooter v-if="inWizard && showsForm">
        <template #start>
          <button
            v-if="step > 0"
            type="button"
            class="btn btn-ghost"
            @click="step--"
          >
            <FontAwesomeIcon icon="arrow-left" class="me-2" />
            {{ $t("common.back") }}
          </button>
          <!-- Every step, unlike the PPA wizard's first only: the full form is the way to the
               settings the wizard skips, wherever the user is when they want them. -->
          <button type="button" class="btn btn-link" @click="view = 'all'">
            <FontAwesomeIcon icon="list" class="me-2" />
            {{ $t("production_strategy.show_all_as_one_form") }}
          </button>
        </template>
        <template #end>
          <span class="text-body-tertiary fs-9">
            {{
              $t("production_strategy.step_x_of_y", {
                current: step + 1,
                total: wizardSteps.length,
              })
            }}
          </span>
          <button
            type="button"
            class="btn btn-ghost"
            @click="modalRef?.handleModalClose()"
          >
            {{ $t("common.cancel") }}
          </button>
          <button
            v-if="!onLastStep"
            type="button"
            class="btn btn-primary"
            :disabled="!currentStep.ready"
            @click="step++"
          >
            {{ $t("common.continue") }}
            <FontAwesomeIcon icon="arrow-right" class="ms-2" />
          </button>
          <ActionButtonWithSpinner
            v-else
            :busy="editor.saving"
            :disabled="!currentStep.ready"
            variant="btn-primary"
            @click="onSave()"
          >
            {{ $t("forwards.mandates.wizard.create") }}
          </ActionButtonWithSpinner>
        </template>
      </WizardFooter>

      <WizardFooter v-else>
        <template #start>
          <span v-if="showsForm && editor.isDirty" role="status">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" class="me-1" />
            {{ $t("forwards.mandates.editor.unsaved_changes") }}
          </span>
        </template>
        <template #end>
          <template v-if="showsForm && editor.isDirty">
            <button
              type="button"
              class="btn btn-sm btn-ghost"
              @click="editor.discard()"
            >
              {{ $t("forwards.mandates.editor.discard") }}
            </button>
            <!-- Force lifts a feasibility block; it cannot lift a body the route will not parse. -->
            <ActionButtonWithSpinner
              v-if="editor.canForceSave"
              :busy="editor.saving"
              :disabled="editor.formInvalid"
              variant="btn-outline-danger"
              @click="onSave(true)"
            >
              {{ $t("forwards.mandates.editor.save_anyway") }}
            </ActionButtonWithSpinner>
            <ActionButtonWithSpinner
              :busy="editor.saving"
              :disabled="!editor.canSave"
              variant="btn-primary"
              @click="onSave()"
            >
              {{ $t("forwards.mandates.editor.save") }}
            </ActionButtonWithSpinner>
          </template>
          <button
            v-else
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="modalRef?.handleModalClose()"
          >
            {{ $t("common.close") }}
          </button>
        </template>
      </WizardFooter>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import WizardFooter from "@/components/common/WizardFooter.vue";
import WizardSection from "@/components/common/WizardSection.vue";
import WizardStepRail from "@/components/common/WizardStepRail.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useUnsavedChangesGuard } from "@/composables/useUnsavedChangesGuard";
import ForwardSalesGuideComponent from "@/forwards2/components/ForwardSalesGuideComponent.vue";
import { readsForwardsAsStaff } from "@/forwards2/forwardsGuard";
import MandateAdvancedListingComponent from "@/forwards2/mandates/MandateAdvancedListingComponent.vue";
import MandateCommitmentsTableComponent from "@/forwards2/mandates/MandateCommitmentsTableComponent.vue";
import MandateDevicePanelComponent from "@/forwards2/mandates/MandateDevicePanelComponent.vue";
import MandateEligibilityGuideComponent from "@/forwards2/mandates/MandateEligibilityGuideComponent.vue";
import MandateEligibilityIndicatorComponent from "@/forwards2/mandates/MandateEligibilityIndicatorComponent.vue";
import MandateEscrowRemainingComponent from "@/forwards2/mandates/MandateEscrowRemainingComponent.vue";
import MandateMembersPickerComponent from "@/forwards2/mandates/MandateMembersPickerComponent.vue";
import MandateNameFieldComponent from "@/forwards2/mandates/MandateNameFieldComponent.vue";
import MandateOptionsCardComponent from "@/forwards2/mandates/MandateOptionsCardComponent.vue";
import MandateProductionRangeComponent from "@/forwards2/mandates/MandateProductionRangeComponent.vue";
import MandateSalesScheduleButtonComponent from "@/forwards2/mandates/MandateSalesScheduleButtonComponent.vue";
import MandateSalesStrategyComponent from "@/forwards2/mandates/MandateSalesStrategyComponent.vue";
import MandateSummaryComponent from "@/forwards2/mandates/MandateSummaryComponent.vue";
import MandateVolumesEditorComponent from "@/forwards2/mandates/MandateVolumesEditorComponent.vue";
import {
  hasSimpleShape,
  usesSimpleListing,
} from "@/forwards2/mandates/mandateListingView";
import {
  MANDATE_DESCRIPTION_MAX,
  MANDATE_NAME_MAX,
} from "@/forwards2/mandates/mandateFormRules";
import { useMandateEditor } from "@/forwards2/mandates/useMandateEditor";
import { uniq } from "lodash-es";
import { computed, onMounted, ref, useId, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteUpdate } from "vue-router";

/**
 * Create and edit a mandate, in a modal: a null `mandateId` opens a blank form, an id an existing mandate.
 * Pages render it through `MandateEditorModalHost`, and it is mounted for one editing session, which
 * ends on `closed`: the host unmounts it. `closed` reports whether the session wrote anything, so
 * the page can reload what it shows.
 *
 * A new mandate is a wizard, as the PPA editor is: companies and devices, then the listing, then a
 * read-only summary with the deal eligibility and a name generated from the choices until its
 * creator edits it. Name, description and options wait for the full form, which an existing mandate
 * opens on and "show all" switches the wizard to, keeping what was entered. A create closes the
 * modal; step 3 has already shown the preview's warnings. Delete closes it too; archive and
 * unarchive reload it.
 *
 * Every save sends the whole form because `PUT /mandates/<id>` replaces the mandate, so a partial body
 * would delete the listings or members it left out. The device allowlist is editable from the
 * start: an unsaved mandate asks the backend for the candidate devices of the companies the form
 * names, so an allowlist mandate takes one save rather than two.
 *
 * A viewer whose only claim on the mandate is escrow it left behind gets the read-only escrow card
 * instead of the form — the response carries nothing else to edit.
 */
const props = defineProps<{
  mandateId: number | null;
}>();

const emit = defineEmits<{
  /** @param written - whether a save, archive, unarchive or delete went through */
  closed: [written: boolean];
}>();

type Section =
  | "warnings"
  | "details"
  | "options"
  | "members"
  | "devices"
  | "listing"
  | "name"
  | "summary"
  | "eligibility"
  | "commitments";

/** A wizard step: its sections, and whether it lets the user go on — the last step's being the create. */
interface WizardStepView {
  title: string;
  sections: readonly Section[];
  ready: boolean;
}

/** The full form's sections (the template sets their order); the commitments only once the mandate exists. */
const FULL_FORM: readonly Section[] = [
  "warnings",
  "details",
  "options",
  "members",
  "devices",
  "listing",
  "eligibility",
  "commitments",
];

const { t } = useI18n();
const editor = useMandateEditor(props.mandateId);
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const accountStore = useAccountStore();
const written = ref(false);
const view = ref<"wizard" | "all">(props.mandateId === null ? "wizard" : "all");
const step = ref(0);

const nameId = useId();
const descriptionId = useId();

const showsForm = computed(
  () => !editor.loading && !editor.notFound && editor.escrowRemaining === null
);
const infeasiblePeriods = computed(
  () => editor.detail?.infeasible_periods ?? []
);
const eligibility = computed(
  () => editor.preview?.eligibility ?? editor.detail?.eligibility ?? null
);

const inWizard = computed(() => view.value === "wizard");

const wizardSteps = computed<WizardStepView[]>(() => [
  {
    title: t("forwards.mandates.wizard.step_members"),
    sections: ["members", "devices"],
    ready: editor.membersReady,
  },
  {
    title: t("forwards.mandates.wizard.step_listing"),
    sections: ["listing"],
    ready: editor.listingReady,
  },
  {
    title: t("forwards.mandates.wizard.step_confirm"),
    sections: ["warnings", "name", "summary", "eligibility"],
    ready: editor.canSave,
  },
]);

const currentStep = computed(() => wizardSteps.value[step.value]);
const onLastStep = computed(() => step.value === wizardSteps.value.length - 1);

const visibleSections = computed<ReadonlySet<Section>>(() => {
  if (inWizard.value) return new Set(currentStep.value.sections);
  return new Set(
    editor.isNew
      ? FULL_FORM.filter((section) => section !== "commitments")
      : FULL_FORM
  );
});

const shows = (section: Section) => visibleSections.value.has(section);

/** Staff choose per mandate between the simple and the advanced listing editor; producers get the choice. */
const isStaff = computed(() => readsForwardsAsStaff(accountStore.account));
const simpleShape = computed(() => hasSimpleShape(editor.form));

const payAsProduced = computed(
  () => editor.form.listing_kind === "pay_as_produced"
);
const salesSchedule = computed(() => editor.preview?.sales_schedule ?? null);

/**
 * Whether the listing shows only a recurring listing's quarters (`usesSimpleListing`). Judged on
 * the form, so the staff's box takes effect as it is ticked.
 */
const simpleListing = computed(() => usesSimpleListing(editor.form));

const heading = computed(() =>
  editor.isNew
    ? t("forwards.mandates.editor.new_title")
    : (editor.detail?.mandate.name ?? t("forwards.mandates.editor.title"))
);

// The last save and the live preview evaluate the same mandate, so they repeat each other's
// sentences; each one is worth showing once (RV-mandates-10).
const allWarnings = computed(() =>
  uniq([...editor.warnings, ...editor.previewWarnings])
);

const descriptionText = computed<string>({
  get: () => editor.form.description ?? "",
  set: (value) => {
    editor.form.description = value === "" ? null : value;
  },
});

// Covers leaving the page while the modal is open; `mayClose` covers the modal's own close controls.
useUnsavedChangesGuard(() => editor.isDirty);

// The load assigns a fresh form, which the editor's form watchers answer with a debounced preview and
// candidate-device refresh — refreshing here as well sent the preview twice on every open.
onMounted(() => {
  if (modalRef.value) modalRef.value.showModal = true;
  void editor.loadCandidateCompanies();
  void editor.load();
});

// A link in the editor that stays on the same route component (the deal page, for another deal)
// unmounts nothing, so the editor closes itself rather than staying open over the new page.
onBeforeRouteUpdate(() => {
  if (!mayClose()) return false;
  finish();
});

const mayClose = () =>
  !editor.isDirty ||
  window.confirm(t("forwards.mandates.editor.close_confirm"));

/** The session's one way out, whichever control or event ended it. */
const finish = () => emit("closed", written.value);

const onNameInput = (event: Event) =>
  editor.setName((event.target as HTMLInputElement).value);

const onSave = async (force = false) => {
  const creating = editor.isNew;
  if ((await editor.save(force ? { force: true } : {})) === null) return;
  written.value = true;
  if (creating) finish();
};

const onDelete = async () => {
  if (!window.confirm(t("forwards.mandates.editor.confirm_delete"))) return;
  if (!(await editor.remove())) return;
  written.value = true;
  finish();
};

const onArchive = async () => {
  if (!(await editor.archive())) return;
  written.value = true;
  await editor.reload();
};

const onUnarchive = async () => {
  if (!(await editor.unarchive())) return;
  written.value = true;
  await editor.reload();
};
</script>

<style scoped lang="scss">
// The sections' own horizontal padding (`WizardSection`), so the guide's toggle lines up with their titles.
.wizard-guide {
  padding: 0 28px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
}
</style>
